/**
 * Analytics Portal V1 — the Analytics Summary dashboard (Phase 10, plan §4.2 + §11 endpoint 3).
 *
 * A pure renderer of `GET /api/analytics/summary`. It owns no metric maths: every number,
 * comparison, notice, availability flag, ranking and label comes from the server. The category
 * filter, date range, comparison toggle, freshness and timezone all live in the Phase 9 shell,
 * so switching category re-requests the endpoint rather than filtering a cached payload.
 */
import React from "react";
import { AudienceBreakdown } from "@/components/analytics/AudienceBreakdown";
import { AvailabilityNotice } from "@/components/analytics/AvailabilityNotice";
import { EmptyState } from "@/components/analytics/EmptyState";
import { KpiCard } from "@/components/analytics/KpiCard";
import { KpiGroup } from "@/components/analytics/KpiGroup";
import { RankingTable } from "@/components/analytics/RankingTable";
import { TrafficSourceChart } from "@/components/analytics/TrafficSourceChart";
import { TrendChart } from "@/components/analytics/TrendChart";
import { SUMMARY_GROUPS, SUMMARY_HEADLINE, scopeTypesOf } from "@/components/analytics/summaryMetrics";
import { useAnalyticsPortal } from "./AnalyticsPortal";

const NO_ACTIVITY_BODY = "Your Analytics Summary will populate once eligible activity is "
  + "collected across the profiles, events and resources you manage.";

export default function SummaryTab() {
  const portal = useAnalyticsPortal();
  const payload = portal?.payload;

  if (!payload) {
    return <div data-testid="analytics-view-summary"><EmptyState state="processing" /></div>;
  }

  const kpis = payload.kpis || {};
  const scope = payload.scope || {};
  const scopeTypes = scopeTypesOf(payload);
  const headline = SUMMARY_HEADLINE.filter((entry) => kpis[entry.key]);

  if (!scope.entities) {
    return <div data-testid="analytics-view-summary"><EmptyState state="no-entities" /></div>;
  }
  // `never_processed` is a freshness state only — the Summary payload still carries the server's
  // supported zero counters, rankings and notices, so it renders with a notice rather than being
  // replaced by an empty state.
  const neverProcessed = payload.freshness?.status === "never_processed";

  return (
    <div className="space-y-7" data-testid="analytics-view-summary">
      <div data-testid="analytics-dashboard-body" className="space-y-7">
        {neverProcessed && (
          <AvailabilityNotice testId="analytics-not-processed-notice" reason="never_processed"
                              notice={NO_ACTIVITY_BODY} />
        )}

        {scope.capped && (
          <AvailabilityNotice
            testId="analytics-summary-scope-capped" reason="partial_scope_cap"
            notice={`This Summary covers the first entities in a very large portfolio (${scope.entities} in scope). Choose a category for exact figures.`} />
        )}

        <section className="space-y-3" aria-label="Portfolio headline">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {headline.map((entry) => (
              <KpiCard key={entry.key} metricKey={entry.key} label={entry.label}
                       hint={entry.hint} metric={kpis[entry.key]}
                       rangeSuggestion={payload.range_suggestion}
                       onUseSupportedRange={portal.onUseSupportedRange} />
            ))}
          </div>
        </section>

        <TrendChart series={payload.series} metricKey="visits" title="Visits over time"
                    unitLabel="visits" description={payload.reporting?.range_label}
                    onChangeRange={portal.onChangeRange} />

        <RankingTable rankings={payload.rankings} onOpenEntity={portal.onOpenEntity} />

        {SUMMARY_GROUPS.map((group) => (
          <KpiGroup key={group.id} {...group} kpis={kpis} scopeTypes={scopeTypes}
                    rangeSuggestion={payload.range_suggestion}
                    onUseSupportedRange={portal.onUseSupportedRange} />
        ))}

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
          <TrafficSourceChart sources={payload.traffic_sources}
                              onChangeRange={portal.onChangeRange} />
          <AudienceBreakdown audience={payload.audience} />
        </div>
      </div>
    </div>
  );
}
